<template>
  <v-container fill-height justify-content-start :class="{'pa-2': $vuetify.breakpoint.xsOnly}" class="app-section-width">
    <v-layout column>
      <v-toolbar color="white" flat>
        <v-btn icon light @click="exit()">
          <v-icon color="grey darken-2">mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title class="grey--text text--darken-4 pa-0">{{getDashboardName({url: $route.path})}}</v-toolbar-title>
      </v-toolbar>
      <v-flex class="app-scroll-section">
        <ValidationObserver ref="settingsForm" v-slot="{ }">
          <v-form class="mt-4" v-if="!changePassword">
            <ValidationProvider :name="$t('fields.sign_in_email')" rules="required|email" v-slot="{ errors }">
              <v-text-field
                :label="$t('fields.sign_in_email')"
                v-model="email"
                :error-messages="edit ? errors: ''"
                :outlined="!edit ? true : false"
                :readonly="!edit ? true : false"
                :filled="edit ? true : false"
                required
              ></v-text-field>
            </ValidationProvider>
            <ValidationProvider :name="$t('fields.language')" rules="required" v-slot="{ errors }">
              <v-select
                :label="$t('fields.language')"
                v-model="language"
                :items="allowedLanguageCodes"
                item-text="name"
                item-value="value"
                :error-messages="edit ? errors: ''"
                :outlined="!edit ? true : false"
                :readonly="!edit ? true : false"
                :filled="edit ? true : false"
                :append-icon="!edit ? '' : '$dropdown'"
              >
              <template v-slot:selection="{ item }">
                {{languages[item].native}}
              </template>
              <template v-slot:item="{ item }">
                {{languages[item].native}}
              </template>
              </v-select>
            </ValidationProvider>
            <v-autocomplete
              :label="$t('fields.display_currency')"
              v-model="displayCurrencyId"
              :items="currencies"
              item-text="currency.name"
              item-value="currency.id"
              :loading="!currencies.length || !displayCurrencyId"
              :outlined="!edit ? true : false"
              :readonly="!edit ? true : false"
              :filled="edit ? true : false"
              :append-icon="!edit ? '' : '$dropdown'"
            />
            <v-btn
              @click="editSettings()"
              v-if="!edit"
              outlined
              block
            >
              {{ $t('actions.edit_settings') }}
            </v-btn>
            <v-btn
              @click="editPassword()"
              v-if="!edit"
              outlined
              block
              class="mt-4"
            >
              {{ $t('actions.change_password') }}
            </v-btn>
            <div class="float-right">
              <v-btn
                @click="cancelEditSettings()"
                v-if="edit"
                outlined
                class="mr-4"
              >
                {{ $t('actions.cancel') }}
              </v-btn>
              <v-btn
                @click="saveSettings()"
                v-if="edit"
                outlined
              >
                {{ $t('actions.save') }}
              </v-btn>
            </div>
          </v-form>
        </ValidationObserver>
        <ValidationObserver ref="passwordForm" v-slot="{ }">
          <v-form class="mt-4" v-if="changePassword">
            <ValidationProvider v-if="hasPassword" :name="$t('fields.old_password')" rules="required" v-slot="{ errors }">
              <v-text-field
                :label="$t('fields.old_password')"
                type="password"
                v-model="oldPassword"
                :error-messages="errors"
                filled
              ></v-text-field>
            </ValidationProvider>
            <ValidationProvider :name="$t('fields.new_password')" rules="required|min:6" v-slot="{ errors }" vid="password">
              <v-text-field
                :label="$t('fields.new_password')"
                type="password"
                v-model="newPassword"
                :error-messages="errors"
                filled
              ></v-text-field>
            </ValidationProvider>
            <ValidationProvider :name="$t('fields.repeat_new_password')" rules="required|confirmed:password" v-slot="{ errors }">
              <v-text-field
                :label="$t('fields.repeat_new_password')"
                type="password"
                v-model="repeatNewPassword"
                :error-messages="errors"
                filled
              ></v-text-field>
            </ValidationProvider>
            <div class="float-right">
              <v-btn
                @click="cancelEditPassword()"
                outlined
                class="mr-4"
              >
                {{ $t('actions.cancel') }}
              </v-btn>
              <v-btn
                @click="savePassword()"
                outlined
              >
                {{ $t('actions.save') }}
              </v-btn>
            </div>
          </v-form>
        </ValidationObserver>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { mapGetters, mapState } from 'vuex'
import routeNames from '../router/routes'
import router from '@/router'
import languages from '@/data/languages'
export default {
  components: {
    ValidationObserver,
    ValidationProvider,
  },
  data () {
    return {
      languages,
      edit: false,
      changePassword: false,
      lastEmail: null,
      lastLanguageCode: null,
      oldPassword: null,
      newPassword: null,
      repeatNewPassword: null,
    }
  },
  methods: {
    setChangePassword: function (value) {
      this.changePassword = value
    },
    exit () {
      router.push({ name: routeNames.main.name })
    },
    editSettings () {
      this.lastEmail = this.email
      this.lastLanguageCode = this.settings.languageCode
      this.edit = true
    },
    cancelEditSettings () {
      this.email = this.lastEmail
      this.settings.languageCode = this.lastLanguageCode
      this.edit = false
    },
    saveSettings () {
      this.$refs.settingsForm.validate().then(success => {
        if (success) {
          this.$store.dispatch('me/putSettings', { email: this.email, languageCode: this.settings.languageCode, displayCurrencyId: this.displayCurrencyId })
          this.$i18n.locale = this.settings.languageCode
          this.edit = false
          this.$store.commit('state/setMessage', { text: this.$t('success.settings'), color: 'success' })
        }
      })
    },
    editPassword () {
      this.resetPasswordForm()
      this.changePassword = true
    },
    cancelEditPassword () {
      this.resetPasswordForm()
      this.changePassword = false
    },
    savePassword () {
      let self = this
      this.$refs.passwordForm.validate().then(success => {
        if (success) {
          this.$store.dispatch('me/putPassword', { oldPassword: this.oldPassword, newPassword: this.newPassword }).then(response => {
            self.resetPasswordForm()
            self.changePassword = false
            self.$store.commit('state/setMessage', { text: self.$t('success.change_password'), color: 'success' })
          }, () => {
            self.$store.commit('state/setMessage', { text: self.$t('error.change_password'), color: 'error' })
          })
        }
      })
    },
    resetPasswordForm () {
      this.oldPassword = null
      this.newPassword = null
      this.repeatNewPassword = null
      this.$refs.passwordForm.reset()
    },
  },
  computed: {
    ...mapState({
      currencies: state => state.me.currencies,
      settings: state => state.me.settings,
    }),
    ...mapGetters('me', ['allowedLanguageCodes', 'defaultLanguageCode']),
    ...mapGetters({
      getDashboardName: 'service/getDashboardName',
    }),
    hasPassword () {
      return this.settings.hasPassword
    },
    email: {
      get () {
        return this.settings.email ? this.settings.email : ''
      },
      set (value) {
        this.settings.email = value
      },
    },
    language: {
      get () {
        if (this.settings.languageCode === undefined || this.settings.languageCode === null) {
          return this.defaultLanguageCode
        }

        var languageCode = this.settings.languageCode

        return languageCode
      },
      set (value) {
        this.settings.languageCode = value
      },
    },
    displayCurrencyId: {
      get () {
        return this.settings.displayCurrencyId || (this.$store.state.me.facility ? this.$store.state.me.facility.baseCurrencyId : null)
      },
      set (value) {
        this.settings.displayCurrencyId = value
      },
    },
  },
  created () {
    this.$store.dispatch('me/getSettings')
  },
}
</script>

<style lang="scss">
#settings {
  max-width: 430px !important;
  padding: 15px 15px 0 15px;

  .v-toolbar__content {
    padding: 0;
  }
}

#content {
  overflow-y: auto;
}
</style>
